<template>
  <div id="app" dir="rtl" class="app-wrapper">
    <seo-meta :page="$route.name" />
    <nav-bar />
    <main class="main-content">
      <router-view />
    </main>
    <footer-component />
  </div>
</template>

<script>
import NavBar from './components/NavBar.vue'
import FooterComponent from './components/FooterComponent.vue'
import SEOComponent from './components/SEO/SEOComponent.vue'

export default {
  name: 'App',
  components: {
    NavBar,
    FooterComponent,
    SeoMeta: SEOComponent
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&family=Tajawal:wght@400;500;700&display=swap');

:root {
  /* Brand Colors */
  --primary-color: #0f4640;
  --primary-light: #1a635b;
  --primary-dark: #0a2f2b;
  
  /* Background Colors */
  --bg-light: #f7f6e1;
  --bg-lighter: #f1f5f9;
  --bg-white: #FFFFFF;
  
  /* Text Colors */
  --text-primary: #0f4640;
  --text-secondary: #526665;
  --text-light: #0b0b0b;
  
  /* Font Sizes */
  --text-xs: 0.75rem;    /* 12px */
  --text-sm: 0.875rem;   /* 14px */
  --text-base: 1rem;     /* 16px */
  --text-lg: 1.125rem;   /* 18px */
  --text-xl: 1.25rem;    /* 20px */
  --text-2xl: 1.5rem;    /* 24px */
  --text-3xl: 1.875rem;  /* 30px */
  --text-4xl: 2.25rem;   /* 36px */
  
  /* Spacing */
  --spacing-xs: 0.5rem;   /* 8px */
  --spacing-sm: 1rem;     /* 16px */
  --spacing-md: 1.5rem;   /* 24px */
  --spacing-lg: 2rem;     /* 32px */
  --spacing-xl: 2.5rem;   /* 40px */
  
  /* Header Height */
  --header-height: 4rem;  /* 64px - adjust this value based on your navbar height */
  
  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(15, 70, 64, 0.05);
  --shadow-md: 0 4px 6px rgba(15, 70, 64, 0.1);
  --shadow-lg: 0 10px 15px rgba(15, 70, 64, 0.1);
  --shadow-xl: 0 20px 25px rgba(15, 70, 64, 0.15);
  
  /* Border Radius */
  --radius-sm: 0.375rem;  /* 6px */
  --radius-md: 0.5rem;    /* 8px */
  --radius-lg: 0.75rem;   /* 12px */
  --radius-xl: 1rem;      /* 16px */
  
  /* Transitions */
  --transition-base: all 0.3s ease;
  --transition-smooth: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Noto Sans Arabic', 'Tajawal', sans-serif;
  color: var(--text-primary);
  line-height: 1.6;
  font-size: var(--text-lg);
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  background-color: var(--bg-white);
}

h1, h2, h3, h4, h5, h6 {
  font-family: 'Cairo', 'Tajawal', sans-serif;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  line-height: 1.3;
}

h1 { font-size: var(--text-4xl); }
h2 { font-size: var(--text-3xl); }
h3 { font-size: var(--text-2xl); }
h4 { font-size: var(--text-xl); }
h5 { font-size: var(--text-lg); }
h6 { font-size: var(--text-base); }

p {
  margin-bottom: var(--spacing-md);
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.8;
}

.app-wrapper {
  min-height: 100vh;
  width: 100vw;
  overflow-x: hidden;
  background: var(--bg-white);
  position: relative;
}

.main-content {
  min-height: 100vh;
  width: 100%;
  background: var(--bg-white);
  transition: all 0.3s ease;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
}

/* Button Styles */
.btn {
  padding: var(--spacing-sm) var(--spacing-lg);
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition-base);
  cursor: pointer;
  font-size: var(--text-base);
}

.btn-primary {
  background-color: var(--primary-color);
  color: var(--text-light);
  border: none;
}

.btn-primary:hover {
  background-color: var(--primary-light);
  transform: translateY(-2px);
}

.btn-secondary {
  background-color: var(--bg-light);
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
}

.btn-secondary:hover {
  background-color: var(--bg-lighter);
  transform: translateY(-2px);
}

/* Card Styles */
.card {
  background: var(--bg-white);
  border-radius: 12px;
  padding: var(--spacing-lg);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Section Styles */
.section {
  padding: var(--spacing-xl) 0;
}

.section-title {
  text-align: center;
  margin-bottom: var(--spacing-lg);
}

/* Remove duplicate hero section styles */
/* Responsive Design */
@media (max-width: 768px) {
  body {
    font-size: 14px;
  }
  
  .container {
    padding: 0 var(--spacing-md);
  }
  
  .main-content {
    width: 100%;
  }
  
  h1 { font-size: 2rem; }
  h2 { font-size: 1.75rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }
  h5 { font-size: 1.1rem; }
  h6 { font-size: 1rem; }
}
</style> 